import { ChatConnectorEntry } from '@upright/ui/chat/chat-connector-entry';
import { BouStatusBar } from '@upright/ui/common/generic/components/bou-status-bar';
import { BouSpinnerSize } from '@upright/ui/common/generic/components/progress/spinners/bou-spinner';
import { CenteredSpinner } from '@upright/ui/common/generic/components/progress/spinners/centered-spinner';
import { breakpoints } from '@upright/ui/common/generic/global/breakpoints';
import { useViewSetting } from '@upright/ui/common/generic/hooks';
import { SCROLLING_CONTAINER_ID } from '@upright/ui/common/generic/layout/layout-constants';
import { HomeViewFrameOutletContext, useBuildHomeViewFrameOutletContext } from '@upright/ui/views/home-views/context';
import {
  SelectedOrganizationProvider,
  useBuildSelectedOrganization,
} from '@upright/ui/views/home-views/selected-organization-context';
import { FooterMaybe } from '@upright/ui/views/root/main/footer-maybe';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { UnifiedCompanyNavigation } from './unified-company-navigation';
import { railClassName, UnifiedInvestorNavigation } from './unified-investor-navigation';
import { UnifiedShellDrawer } from './unified-shell-drawer';
import { UnifiedShellSearch } from './unified-shell-search';
import styles from './unified-shell-frame.module.css';
import { isChatPath } from './unified-shell-chat-routes';
import { UnifiedTopBar } from './unified-top-bar';
import { useUnifiedShellAudience } from './use-unified-shell-audience';

/**
 * The unified shell's frame around the investor home views (PLT-6539): the white top bar,
 * the rail on the left and a content column that scrolls inside itself. It stands in for
 * `HomeViewFrame` — `BouPageWithFrame` under the dark header — for a user `useUnifiedShell`
 * opts in. The route tree picks between the two (`root/main/routes/index.tsx`), and the
 * header switch renders no header of its own while this frame is up, because the bar is
 * part of the frame the way the corporate shell's is.
 *
 * The rail's *content* is `UnifiedInvestorNavigation` (PLT-6528) — the organisation selector,
 * then titled collapsible groups — and not the shipped `MainNavigation` the legacy frame mounts,
 * which stays as it is for everyone the flag has not reached. A company front page gets
 * `UnifiedCompanyNavigation` instead, built from the same pieces (PLT-6943).
 *
 * `suspended` renders the frame with a spinner in the content column while a lazy view
 * loads, so the chrome does not flash out and back in between routes — the contract
 * `HomeViewFrame` has.
 */
const UnifiedShellFrameContext = createContext(false);

export function useInUnifiedShellFrame() {
  return useContext(UnifiedShellFrameContext);
}

export function UnifiedShellFrame(props: {
  suspended?: boolean;
  children?: ReactNode;
  banner?: ReactNode;
  entityNavigation?: ReactNode;
}) {
  const context: HomeViewFrameOutletContext = useBuildHomeViewFrameOutletContext();
  const selectedOrganizationContext = useBuildSelectedOrganization();
  const showStatusBar = useViewSetting('internalAdmin.showStatusBar');
  // Chat is the one view that owns its own scrolling: the thread scrolls while the composer
  // stays put at the foot of the column (PLT-6610). Every other view scrolls the column itself.
  const { pathname } = useLocation();
  const contentScrollsItself = isChatPath(pathname);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // A company front page gets the company variant of the rail (PLT-6943); everyone else the
  // investor one.
  const isCompanyFrontPage = useUnifiedShellAudience() === 'company';
  const [railElement, setRailElement] = useState<HTMLElement | null>(null);
  // The drawer takes the rail column while it is open, so the watch restarts on the new column when
  // the drawer closes.
  const rowsBelowFold = useRowsBelowFold(railElement, isDrawerOpen);

  /*
   * Close the drawer when the viewport widens past `md`, where the rail is a pane again and the
   * menu button that opened it is gone. Without this, a reader who opens the drawer on a phone and
   * rotates to landscape is left with a scrim over a shell whose rail is already beside them, and
   * no visible way out. A viewport media query rather than a container one: this is about the
   * window, and it is the same step the stylesheet switches the pane at.
   */
  useEffect(() => {
    const wideEnoughForPane = window.matchMedia(`(min-width: ${breakpoints[1]})`);
    const closeIfWide = (event: MediaQueryList | MediaQueryListEvent) => {
      if (event.matches) setIsDrawerOpen(false);
    };
    closeIfWide(wideEnoughForPane);
    wideEnoughForPane.addEventListener('change', closeIfWide);
    return () => {
      wideEnoughForPane.removeEventListener('change', closeIfWide);
    };
  }, []);

  /*
   * Any navigation closes the drawer. Keyed on the pathname rather than on a click handler inside
   * it, because the drawer holds three kinds of control that all navigate — rail rows, the
   * organisation selector's menu, and the search's result list, the last two of which navigate
   * programmatically and would slip past a click listener. Leaving it open over the page the
   * reader just asked for would make every journey two taps. Clicking the row you are already on
   * changes no pathname and correctly does nothing.
   */
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [pathname]);

  /*
   * One rail, in one place at a time. `UnifiedInvestorNavigation` subscribes to the chat runtime
   * and keeps the conversation list in sync (`useChatConversationListSync`), so mounting it in the
   * pane and the drawer at once would run that subscription twice against one query cache. The
   * pane is hidden below `md` by the stylesheet, and the drawer only opens there, so exactly one
   * of these two is ever visible — this makes exactly one of them ever exist.
   *
   * The rail ends with its utilities (design/40-shell-and-pages.md "Sidebar"): the connector entry,
   * pinned to the foot while the rows are shorter than the rail and sticky once they scroll
   * (PLT-8002). It renders nothing for a user without connector access, and the foot then
   * collapses with it. An entity page's rail is the entity's views and nothing else, so it has no
   * foot. The foot carries the rows' `railClassName` for their label size, and in the drawer the
   * entry opens its popover above the row, because the drawer leaves no room beside it. While rows
   * are below the fold, the rows fade to white above the foot (`useRowsBelowFold`).
   */
  const rail = (
    <div className={styles.railColumn}>
      {isCompanyFrontPage ? <UnifiedCompanyNavigation /> : <UnifiedInvestorNavigation />}
      <div className={`${styles.railFoot} ${railClassName}`}>
        <ChatConnectorEntry variant={isDrawerOpen ? 'drawer' : 'rail'} />
      </div>
    </div>
  );

  return (
    <UnifiedShellFrameContext.Provider value={true}>
      <SelectedOrganizationProvider value={selectedOrganizationContext}>
        <div className={styles.frame}>
          <UnifiedTopBar
            onOpenNavigation={() => {
              setIsDrawerOpen(true);
            }}
          />
          {props.banner}
          <div className={styles.body}>
            {/* The rail renders the `<nav>` landmark itself, so this is a plain region. */}
            <aside
              ref={setRailElement}
              className={props.entityNavigation ? `${styles.rail} ${styles.railSolid}` : styles.rail}
              data-rows-below-fold={rowsBelowFold || undefined}
            >
              {props.entityNavigation ?? (!isDrawerOpen && rail)}
            </aside>
            <UnifiedShellDrawer
              isOpen={isDrawerOpen}
              onClose={() => {
                setIsDrawerOpen(false);
              }}
              search={<UnifiedShellSearch variant="drawer" />}
            >
              {isDrawerOpen && rail}
            </UnifiedShellDrawer>
            {/*
             * The id is what `scroll-utils` scrolls: the investor views call it expecting the
             * frame, not the document, to be the scrolling element.
             */}
            <main
              id={SCROLLING_CONTAINER_ID}
              className={contentScrollsItself ? `${styles.content} ${styles.contentOwnScroll}` : styles.content}
            >
              {props.suspended ? (
                <div className={styles.spinner}>
                  <CenteredSpinner size={BouSpinnerSize.MEDIUM} />
                </div>
              ) : (
                (props.children ?? <Outlet context={context} />)
              )}
              <FooterMaybe />
            </main>
          </div>
          {showStatusBar && (
            <div className={styles.statusBar}>
              <BouStatusBar />
            </div>
          )}
        </div>
      </SelectedOrganizationProvider>
    </UnifiedShellFrameContext.Provider>
  );
}

/**
 * Whether part of the rail's rows is below its fold, kept current as the rail scrolls and as its
 * rows or its own height change (a group collapsing, the window resizing); the rail fades its rows
 * out above the foot while it is (PLT-8068). A pixel of slack, because fractional heights leave a
 * sub-pixel remainder at the very bottom. `isDrawerOpen` restarts the watch: the rail column the
 * observer follows is unmounted while the drawer holds the rail, and a new one mounts when it closes.
 */
function useRowsBelowFold(rail: HTMLElement | null, isDrawerOpen: boolean): boolean {
  const [belowFold, setBelowFold] = useState(false);
  useEffect(() => {
    if (!rail) return undefined;
    const update = () => {
      setBelowFold(rail.scrollTop + rail.clientHeight < rail.scrollHeight - 1);
    };
    update();
    rail.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(rail);
    if (rail.firstElementChild) observer.observe(rail.firstElementChild);
    return () => {
      rail.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [rail, isDrawerOpen]);
  return belowFold;
}
